/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import React from "react";
import { Card } from "@material-ui/core";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../../utility/errorCodes";
import { RoleDetails } from "./RoleDetails";
import { v4 as uuid } from "uuid";
import { RoleList } from "./RoleList";
import { getAllRoles, deleteRole, updateRole } from "../../../services/UserService";
import {
	ROLE_PERMISSION_GROUPS,
	permissionExists,
	DEFAULT_ADMIN_ROLE,
	DEFAULT_AGENT_ROLE,
} from "../../../../../utility/permissions";
import { useDashboardUrlConfigs } from "../../../../dashboardManager/context/Provider";
import {
	ContentLayout,
	LeftPanelLayout,
	PanelContainer,
	ProgressOverlay,
	RightPanelLayout,
} from "../../../../../components/Common";
import { useCallback } from "react";
import { useEffect } from "react";

const COMPONENT_NAME = "Admin settingsManager/UserManagement/RoleManagement/PermissionTab.jsx";

/**
 * This component deals with roleList and roleDetails component.
 */
export const PermissionTab = ({ showSnackbar, setTabModified }) => {
	const [roles, setRoles] = React.useState(undefined);
	const [filteredRoles, setFilteredRoles] = React.useState(undefined);
	const [selectedRole, setSelectedRole] = React.useState(undefined);
	const [selectedRoleId, setSelectedRoleId] = React.useState(undefined);
	const [pageModules, setPageModules] = React.useState(undefined);
	const [loading, setFormLoading] = React.useState(false);
	const [searchTerm, setSearchTerm] = React.useState("");
	const [edited, setEdited] = React.useState(false);
	const [saving, setSaving] = React.useState(false);
	const { enableDashboards, dashboardUrls } = useDashboardUrlConfigs();

	const setLoading = React.useCallback((newLoading) => {
		setFormLoading(newLoading);
	}, []);

	useEffect(() => {
		if (edited) {
			setTabModified(true);
		}
	}, [edited, setTabModified]);

	const onSearchTermChange = React.useCallback((newSearchTerm, roles) => {
		const searchTerm = newSearchTerm.toLowerCase();
		const filteredRolesList =
			roles &&
			roles
				.filter((x) => x.roleName.toLowerCase().includes(searchTerm))
				.sort((a, b) => (a.roleName.toLowerCase() > b.roleName.toLowerCase() ? 1 : -1));
		setSearchTerm(newSearchTerm);
		setFilteredRoles(filteredRolesList);
	}, []);

	const clearPanel = useCallback(() => {
		setSelectedRole(undefined);
		setSelectedRoleId(undefined);
		setEdited(false);
		setTabModified(false);
	}, [setSelectedRole, setSelectedRoleId, setEdited, setTabModified]);

	const saveRole = React.useCallback(
		async (updatedRole) => {
			const isNewRole = !!updatedRole?.newRole;
			try {
				setSaving(true);
				let permissionsList = [];
				const { newRole, permissions, ...roleUpdateObject } = updatedRole;
				//On the front end permissions is an object for easier use.
				//On the backend we store it as a list.
				Object.keys(permissions).forEach((permissionKey) => {
					if (permissions[permissionKey]) {
						permissionsList.push(permissionKey);
					}
				});
				const updated = { ...roleUpdateObject, permissions };
				setEdited(false);
				await updateRole({ ...roleUpdateObject, permissionsList });
				setSelectedRole(updated);
				// Add role to list.
				setRoles((oldRoles) => {
					const newRoles = [...oldRoles];
					if (newRole) {
						newRoles.push(updated);
					} else {
						newRoles[newRoles.findIndex((role) => role.roleId === roleUpdateObject.roleId)] = updated;
					}
					onSearchTermChange(searchTerm, newRoles);
					return newRoles;
				});
				setSelectedRoleId(updated.roleId);
				showSnackbar(`Successfully ${isNewRole ? "created" : "saved"} role.`, "success");
				setTabModified(false);
				clearPanel();
			} catch (error) {
				v2.error(`Error while saving role.`, {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.UPDATE_THIRD_PARTY_APPLICATIONS_ERROR,
				});
				showSnackbar(`Failed to ${isNewRole ? "create " : "save"} role.`, "error");
			} finally {
				setSaving(false);
			}
		},
		[showSnackbar, onSearchTermChange, clearPanel, searchTerm, setTabModified],
	);

	React.useEffect(() => {
		const fetchRoles = async () => {
			try {
				setLoading(true);
				const newRoles = await getAllRoles();

				const allRoles = [...newRoles, { ...DEFAULT_ADMIN_ROLE }, { ...DEFAULT_AGENT_ROLE }].sort((a, b) =>
					a.roleName.toLowerCase() > b.roleName.toLowerCase() ? 1 : -1,
				);

				//converting role permission list to object for easier management.
				allRoles.forEach((role) => {
					const rolePermissions = {};
					role.permissionsList.forEach((permissionId) => {
						if (permissionExists(permissionId) || dashboardUrls.find((x) => x.reportId === permissionId)) {
							rolePermissions[permissionId] = true;
						}
					});
					delete role.permissionsList;
					role.permissions = rolePermissions;
				});

				let newPageLayout = [];
				ROLE_PERMISSION_GROUPS.forEach((navModule) => {
					const pageModule = { ...navModule, permissions: [...navModule.permissions] };
					if (pageModule.id === "Dashboards") {
						dashboardUrls.forEach((x) => {
							pageModule.permissions.push({ id: x.reportId, label: x.reportName });
						});
						if (!enableDashboards || pageModule.permissions.length === 0) {
							pageModule.disabled = true;
						}
					}
					newPageLayout.push(pageModule);
				});
				setRoles(allRoles);
				setFilteredRoles(allRoles);
				setPageModules(newPageLayout);
			} catch (error) {
				v2.error(`Error while fetching roles.`, {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.GET_SOCIAL_MEDIA_APPLICATIONS_ERROR,
				});
				setRoles([]);
				setFilteredRoles([]);
				setPageModules([]);
			} finally {
				setLoading(false);
			}
		};
		if (!roles && !loading) {
			setLoading(true);
			fetchRoles();
		}
	}, [loading, roles, setLoading, dashboardUrls, enableDashboards]);

	const removeRole = React.useCallback(async () => {
		try {
			setSaving(true);
			await deleteRole(selectedRoleId);
			setRoles((oldRoles) => {
				const newRoles = [...oldRoles];

				newRoles.splice(
					newRoles.findIndex((deleteRole) => deleteRole.roleId === selectedRoleId),
					1,
				);
				onSearchTermChange(searchTerm, newRoles);
				return newRoles;
			});
			setSelectedRole(undefined);
			setSelectedRoleId(undefined);
			showSnackbar("Successfully removed role.", "success");
		} catch (error) {
			v2.error(`Error while deleting role.`, {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.DELETE_THIRD_PARTY_APPLICATIONS_ERROR,
			});
			showSnackbar("Failed to remove role", "error");
		} finally {
			setSaving(false);
		}
	}, [selectedRoleId, showSnackbar, setSaving, onSearchTermChange, searchTerm]);

	return (
		<ContentLayout style={{ marginTop: 0 }}>
			{loading && <ProgressOverlay />}
			<PanelContainer>
				<LeftPanelLayout>
					<RoleList
						selectRole={(selectedRole) => {
							setTabModified(false);
							setSelectedRole({ ...selectedRole });
							setSelectedRoleId(selectedRole.roleId);
							setEdited(false);
						}}
						createNewRole={() => {
							setSelectedRole({
								roleName: "",
								roleId: uuid(),
								permissions: {},
								newRole: true,
							});
							setTabModified(true);
							setSelectedRoleId(undefined);
							setEdited(false);
						}}
						formEdited={edited}
						searchTermLabel={"Search by Role"}
						searchTerm={searchTerm}
						selectedRoleId={selectedRoleId}
						filteredRolesList={filteredRoles}
						onSearchTermChange={(newSearchTerm) => onSearchTermChange(newSearchTerm, roles)}
					/>
				</LeftPanelLayout>
				<RightPanelLayout>
					<Card style={{ width: "100%", minHeight: "600px" }}>
						<RoleDetails
							selectedRole={selectedRole}
							pageModules={pageModules}
							removeRoleHandler={removeRole}
							saving={saving}
							setFormEdited={setEdited}
							formEdited={edited}
							roles={roles || {}}
							selectedRoleId={selectedRoleId}
							cancelHandler={clearPanel}
							saveRoleHandler={saveRole}
						/>
					</Card>
				</RightPanelLayout>
			</PanelContainer>
		</ContentLayout>
	);
};
